import React from 'react';
import { Users, Building2, RotateCcw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export const DASHBOARD_FILTERS_KEY = 'tms_dashboard_filters';
export const LOADS_FILTERS_KEY = 'tms_loads_team_filters';
export const EMPTY_FILTERS = { operatorId: '', customerId: '' };

export const readFilters = (storage, key) => {
  try { return { ...EMPTY_FILTERS, ...(JSON.parse(storage.getItem(key) || 'null') || {}) }; } catch { return { ...EMPTY_FILTERS }; }
};

const ROLE_LABEL = { account_manager: 'Account Manager', operator: 'Operator', support_operator: 'Support Operator' };

// Returns the predicate used by both Dashboard and Loads: team membership (customer-based) AND customer
export const makeLoadFilter = (filters, directory) => {
  const person = filters.operatorId ? (directory?.people || []).find(p => p.id === filters.operatorId) : null;
  const personCustomers = person ? new Set(person.customer_ids) : null;
  return (load) => {
    if (filters.operatorId && !(personCustomers && personCustomers.has(load.customer_id))) return false;
    if (filters.customerId && load.customer_id !== filters.customerId) return false;
    return true;
  };
};

// Shared "All Operators" / "All Customers" filters with visible selections and Reset
export const TeamCustomerFilters = ({ filters, onChange, directory, compact = false, testPrefix = 'team-filter' }) => {
  const people = directory?.people || [];
  const customers = directory?.customers || [];
  const person = people.find(p => p.id === filters.operatorId);
  const customer = customers.find(c => c.id === filters.customerId);
  const active = !!(filters.operatorId || filters.customerId);
  const h = compact ? 'h-8' : 'h-9';

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testPrefix}-bar`}>
      <Select value={filters.operatorId || 'all'} onValueChange={(v) => onChange({ ...filters, operatorId: v === 'all' ? '' : v })}>
        <SelectTrigger className={`w-[calc(50vw-22px)] sm:w-56 ${h} text-sm ${filters.operatorId ? 'border-[var(--tms-primary)] bg-[var(--tms-primary)]/5' : ''}`} data-testid={`${testPrefix}-operator`}>
          <Users className="w-3.5 h-3.5 mr-1.5 text-[var(--tms-text-muted)]" />
          <SelectValue placeholder="All Operators">{person ? `${person.name} · ${ROLE_LABEL[person.role] || person.role}` : 'All Operators'}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Operators</SelectItem>
          {people.map(p => (
            <SelectItem key={p.id} value={p.id} data-testid={`${testPrefix}-operator-option-${p.id}`}>
              <span className="flex items-center gap-2">{p.name}<span className="text-[10px] uppercase tracking-wide text-[var(--tms-text-muted)]">{ROLE_LABEL[p.role] || p.role}</span></span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.customerId || 'all'} onValueChange={(v) => onChange({ ...filters, customerId: v === 'all' ? '' : v })}>
        <SelectTrigger className={`w-[calc(50vw-22px)] sm:w-56 ${h} text-sm ${filters.customerId ? 'border-[var(--tms-primary)] bg-[var(--tms-primary)]/5' : ''}`} data-testid={`${testPrefix}-customer`}>
          <Building2 className="w-3.5 h-3.5 mr-1.5 text-[var(--tms-text-muted)]" />
          <SelectValue placeholder="All Customers">{customer ? customer.name : 'All Customers'}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Customers</SelectItem>
          {customers.map(c => <SelectItem key={c.id} value={c.id} data-testid={`${testPrefix}-customer-option-${c.id}`}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {active && (
        <button
          type="button"
          onClick={() => onChange({ ...EMPTY_FILTERS })}
          className={`inline-flex items-center gap-1 px-3 ${h} rounded-md border border-[var(--tms-border)] text-xs font-medium text-[var(--tms-text-secondary)] hover:bg-[var(--tms-hover)]`}
          data-testid={`${testPrefix}-reset`}
        >
          <RotateCcw className="w-3 h-3" /> Reset Filters
        </button>
      )}
    </div>
  );
};
